// Studio helper for every locked S3 bucket: raw S3 reads of these buckets return 403, so
// a raw URL (stored in an old project, or sent by an API) only loads on the bucket's CDN.
// The 4 regional upload buckets (./uploadsCdn) and the 5 TFI #3340 buckets
// (./media-hosts) keep their own maps; this module adds the other locked buckets and is
// the one entry point for all of them. Only the host is swapped: the S3 object key is
// never changed. Hosts probed 2026-11-01: raw 403, CDN 206 with ACAO `*`.
import { CONVERTED_WEBM_CDN_REWRITE, toConvertedWebmCdnUrl } from './convertedWebmCdn';
import { canonicalUrl } from './media-hosts/mediaHosts';
import { toUploadsCdnUrl, unsignedSearch, uploadsBucketOfCdnHost } from './uploadsCdn';

// Rollout switches, one per bucket group (unchanged URLs while off).
// Legacy flat upload buckets.
export const LEGACY_UPLOADS_CDN_REWRITE = true;
// thumbnail-maker output (scene and video thumbnails).
export const THUMBNAILS_CDN_REWRITE = true;
// Static asset buckets: element JSONs, stock-media, renderer scripts, studio web assets.
export const STATIC_BUCKETS_CDN_REWRITE = true;
// usw2 export bucket (rendered videos and their thumbnails), for old raw values.
export const EXPORTS_CDN_REWRITE = true;

export type LockedBucketGroup =
  | 'legacy-uploads'
  | 'thumbnails'
  | 'static'
  | 'exports'
  | 'converted-webm';

const GROUP_ENABLED: Record<LockedBucketGroup, boolean> = {
  'legacy-uploads': LEGACY_UPLOADS_CDN_REWRITE,
  thumbnails: THUMBNAILS_CDN_REWRITE,
  static: STATIC_BUCKETS_CDN_REWRITE,
  exports: EXPORTS_CDN_REWRITE,
  // converted-webm-videos has its own mapper and switch (./convertedWebmCdn), shared with
  // the editor optimisers
  'converted-webm': CONVERTED_WEBM_CDN_REWRITE,
};
const ALL_GROUPS = Object.keys(GROUP_ENABLED) as LockedBucketGroup[];

interface LockedBucket {
  region: string;
  cdnHost: string;
  group: LockedBucketGroup;
}

// bucket -> its region (raw hosts must match it exactly) and the CDN host that serves the
// bucket root with the same keys
const LOCKED_BUCKETS: Record<string, LockedBucket> = {
  'invideo-uploads': {
    region: 'ap-south-1',
    cdnHost: 'd3jrdn1nm7thr1.cloudfront.net',
    group: 'legacy-uploads',
  },
  'invideo-uploads-image': {
    region: 'ap-south-1',
    cdnHost: 'd3lpxzh90xab0r.cloudfront.net',
    group: 'legacy-uploads',
  },
  'invideo-uploads-videos': {
    region: 'ap-south-1',
    cdnHost: 'd1kr40z4o5df3w.cloudfront.net',
    group: 'legacy-uploads',
  },
  'invideo-uploads-audio': {
    region: 'ap-south-1',
    cdnHost: 'd1ahsq9yki2fk4.cloudfront.net',
    group: 'legacy-uploads',
  },
  'invideo-thumbnails': {
    region: 'ap-south-1',
    cdnHost: 'thumbnails-cdn.invideo.io',
    group: 'thumbnails',
  },
  'dummy-video-json': {
    region: 'ap-south-1',
    cdnHost: 'dummy-video-json-cdn.invideo.io',
    group: 'static',
  },
  'stock-media': {
    region: 'ap-south-1',
    cdnHost: 'd2l7cu8sa63nxt.cloudfront.net',
    group: 'static',
  },
  'ttv-scripts': {
    region: 'us-east-2',
    cdnHost: 'd3a303i0ojda9n.cloudfront.net',
    group: 'static',
  },
  'iv-fe-web-assets': {
    region: 'ap-south-1',
    cdnHost: 'web-assets.invideo.io',
    group: 'static',
  },
  // This CDN sends no Access-Control-Allow-Origin (probed 2026-11-01): fine for a plain
  // <img>/<video>, not for crossOrigin/canvas/fetch reads. Nothing here sets crossOrigin,
  // and the slate (which reads media with CORS) does not map this group.
  'iv-usw2-exported-videos': {
    region: 'us-west-2',
    cdnHost: 'd14jnjw9sed842.cloudfront.net',
    group: 'exports',
  },
};

type RawForm = 'rest' | 'website';

function lockedBucketAndKey(parsed: URL): [string, string, RawForm] | null {
  const host = parsed.hostname.toLowerCase();
  for (const bucket of Object.keys(LOCKED_BUCKETS)) {
    const { region } = LOCKED_BUCKETS[bucket];
    if (
      host === `${bucket}.s3.${region}.amazonaws.com` ||
      host === `${bucket}.s3-${region}.amazonaws.com` ||
      host === `${bucket}.s3.amazonaws.com`
    ) {
      return [bucket, parsed.pathname.replace(/^\//, ''), 'rest'];
    }
    if (
      host === `${bucket}.s3-website-${region}.amazonaws.com` ||
      host === `${bucket}.s3-website.${region}.amazonaws.com`
    ) {
      return [bucket, parsed.pathname.replace(/^\//, ''), 'website'];
    }
    if (
      (host === `s3.${region}.amazonaws.com` ||
        host === `s3-${region}.amazonaws.com` ||
        host === 's3.amazonaws.com') &&
      parsed.pathname.startsWith(`/${bucket}/`)
    ) {
      return [bucket, parsed.pathname.slice(bucket.length + 2), 'rest'];
    }
  }
  return null;
}

function isRawS3Host(url: string): boolean {
  try {
    return new URL(url).hostname.toLowerCase().endsWith('.amazonaws.com');
  } catch (e) {
    return false;
  }
}

/**
 * Maps a raw S3 URL (virtual-host, path-style or website, exact hosts only) of one of
 * this module's buckets (legacy uploads, thumbnails, static assets, exports, webm
 * conversions) to its CDN,
 * limited to `groups` when given. Anything else, CDN URLs included, is returned unchanged.
 */
export function toOtherLockedBucketCdnUrl<T extends string | null | undefined>(
  url: T,
  groups: LockedBucketGroup[] = ALL_GROUPS,
): T {
  if (typeof url !== 'string' || !url.includes('amazonaws.com')) {
    return url;
  }
  if (groups.includes('converted-webm')) {
    const webmCdnUrl = toConvertedWebmCdnUrl(url);
    if (webmCdnUrl !== url) {
      return webmCdnUrl as T;
    }
  }
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch (e) {
    return url;
  }
  if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
    return url;
  }
  const media = lockedBucketAndKey(parsed);
  if (!media || !media[1]) {
    return url;
  }
  const { group } = LOCKED_BUCKETS[media[0]];
  if (!GROUP_ENABLED[group] || !groups.includes(group)) {
    return url;
  }
  const [bucket, path, form] = media;
  // S3 REST reads a raw '+' in the path as a space, the website endpoint as a plus; the
  // CDN reads %20 as a space and %2B as a plus. Other %XX stay as they are.
  const key = path.replace(/\+/g, form === 'website' ? '%2B' : '%20');
  return `https://${LOCKED_BUCKETS[bucket].cdnHost}/${key}${unsignedSearch(parsed.search)}${
    parsed.hash
  }` as T;
}

/**
 * The one mapper for every locked bucket: a raw S3 URL of the 4 regional upload buckets,
 * the 5 media-hosts buckets, the legacy upload buckets, invideo-thumbnails, the static
 * asset buckets, the usw2 export bucket or converted-webm-videos, on its CDN host with the same key. Anything else (CDN URLs, other hosts
 * and buckets, non-URLs) is returned unchanged. Pure string work: never fetches.
 */
export function toLockedBucketCdnUrl<T extends string | null | undefined>(url: T): T {
  if (typeof url !== 'string' || !url.includes('amazonaws.com') || !isRawS3Host(url)) {
    return url;
  }
  const uploadsCdnUrl = toUploadsCdnUrl(url);
  if (uploadsCdnUrl !== url) {
    return uploadsCdnUrl;
  }
  let mediaCdnUrl: string | null = null;
  try {
    mediaCdnUrl = canonicalUrl(url);
  } catch (e) {
    // the shared port throws on a lone surrogate: leave such a URL to the table below
  }
  if (mediaCdnUrl) {
    return mediaCdnUrl as T;
  }
  return toOtherLockedBucketCdnUrl(url);
}

/**
 * The raw S3 URL (virtual-host, same key, query and fragment) of a URL on the CDN host of
 * one of `buckets` (regional upload buckets or this module's buckets), for code that only
 * derives copies from the raw form. Exact hosts only: https, no userinfo or port, and no
 * S3 or CloudFront signing params (a signed URL only works as is). The key is kept as is:
 * these REST-origin CDNs read `+` as a space, like raw S3. Null for anything else.
 */
export function toLockedBucketRawS3Url(url: string, buckets: string[]): string | null {
  let parsed: URL;
  try {
    parsed = new URL(url);
  } catch (e) {
    return null;
  }
  if (
    parsed.protocol !== 'https:' ||
    parsed.username ||
    parsed.password ||
    parsed.port ||
    unsignedSearch(parsed.search) !== parsed.search
  ) {
    return null;
  }
  const host = parsed.hostname.toLowerCase();
  const target =
    uploadsBucketOfCdnHost(host) ??
    Object.keys(LOCKED_BUCKETS)
      .filter((bucket) => LOCKED_BUCKETS[bucket].cdnHost === host)
      .map((bucket): [string, string] => [bucket, LOCKED_BUCKETS[bucket].region])[0] ??
    null;
  const key = parsed.pathname.replace(/^\//, '');
  if (!target || !buckets.includes(target[0]) || !key) {
    return null;
  }
  const [bucket, region] = target;
  return `https://${bucket}.s3.${region}.amazonaws.com/${key}${parsed.search}${parsed.hash}`;
}
